import type {
  AuthenticatedUser,
  BackupCodes,
  ChangePasswordInput,
  Confirm2faInput,
  ConfirmEmailChangeInput,
  ConfirmEmailChangeResult,
  ConfirmEmailInput,
  ConfirmEnrollRequiredInput,
  ConfirmPasswordResetInput,
  DeleteAccountInput,
  EnrollmentRequired,
  Enrol2faResponse,
  Enroll2faInput,
  RecoveryCodesStatus,
  RequestEmailChangeInput,
  RequestPasswordResetInput,
  Signin2faInput,
  Signin2faRecoveryInput,
  SigninInput,
  SignupChallenge,
  SignupInput,
  TwoFactorRequired,
} from '@projoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiMessages from '@/shared/i18n/en/lib-api';
import { tr } from '@/shared/i18n/translate';
import { signupLocaleFields, syncProfileLocale } from '@/shared/i18n/boot';

import { releasePushDevice } from '@/shared/pwa/push-device';

import { ApiError, api, idempotencyKey } from './client';
import { captureAuthLifetime, isAuthLifetimeCurrent, useAuthStore } from '@/shared/stores/auth.store';
import { finishSecurityChange } from '@/shared/lib/security-sign-out';

interface SignedIn {
  user: AuthenticatedUser;
  accessToken: string;
  // No refreshToken — it arrives only as the httpOnly cookie, unreadable by JS.
}

type SigninResponse = SignedIn | TwoFactorRequired | EnrollmentRequired;

// ── mutations ────────────────────────────────────────────────────────────────

export function useSignup() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: SignupInput): Promise<SignedIn> => {
      // The api returns `{ kind: 'signed_in', user, accessToken, refreshToken }`.
      const res = await api<SignedIn & { kind?: string }>('/api/auth/signup', {
        method: 'POST',
        // A new account starts in the language it was created in.
        body: { ...signupLocaleFields(), ...input },
        unauthenticated: true,
        idempotencyKey: idempotencyKey(),
      });
      return res;
    },
    onSuccess: (s) => setSession(s),
  });
}

/**
 * Bot checks for the sign-up form: a signed start-of-form token (the server
 * refuses a submit faster than `minFillMs`) and the CAPTCHA site key when the
 * operator turned one on. Fetched once per visit to the page.
 */
export function useSignupChallenge() {
  return useQuery({
    queryKey: ['auth', 'signup-challenge'],
    queryFn: () => api<SignupChallenge>('/api/auth/signup/challenge', { unauthenticated: true }),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });
}

export function useSignin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    // The login page renders its own error UI (toast / 429 countdown) — skip
    // the global mutation toast so errors don't show twice.
    meta: { silentErrors: true },
    mutationFn: (input: SigninInput) =>
      api<SigninResponse>('/api/auth/signin', {
        method: 'POST',
        body: input,
        unauthenticated: true,
      }),
    onSuccess: (res) => {
      // Caller routes to the 2FA-verify or 2FA-enrol stage in those cases.
      if ('twoFactorRequired' in res || 'enrollmentRequired' in res) return;
      setSession(res);
    },
  });
}

export function useSignin2fa() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    // The login page renders its own error UI (toast / 429 countdown) — skip
    // the global mutation toast so errors don't show twice.
    meta: { silentErrors: true },
    mutationFn: (input: Signin2faInput) =>
      api<SignedIn>('/api/auth/signin/2fa', {
        method: 'POST',
        body: input,
        unauthenticated: true,
      }),
    onSuccess: (s) => setSession(s),
  });
}

/** Login-time 2FA enrolment — step 1: fetch the QR/secret using the
 *  enrolment ticket from a `enrollmentRequired` sign-in response. */
export function useEnroll2faRequired() {
  return useMutation({
    // The login page renders its own error UI (toast / 429 countdown) — skip
    // the global mutation toast so errors don't show twice.
    meta: { silentErrors: true },
    mutationFn: (ticket: string) =>
      api<Enrol2faResponse>('/api/auth/2fa/enroll-required', {
        method: 'POST',
        body: { ticket },
        unauthenticated: true,
      }),
  });
}

/** Login-time 2FA enrolment — step 2: confirm the code, which enables 2FA AND
 *  completes sign-in (the response is a normal signed-in session). */
export function useConfirm2faEnrollment() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    // The login page renders its own error UI (toast / 429 countdown) — skip
    // the global mutation toast so errors don't show twice.
    meta: { silentErrors: true },
    mutationFn: (input: ConfirmEnrollRequiredInput) =>
      api<SignedIn & BackupCodes>('/api/auth/2fa/enroll-required/confirm', {
        method: 'POST',
        body: input,
        unauthenticated: true,
      }),
    // Sign the user in immediately; the login page then shows the recovery
    // codes (from the response) on a final screen before navigating away.
    onSuccess: (s) => setSession({ user: s.user, accessToken: s.accessToken }),
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (allSessions: boolean = false) => {
      // First, while the session still works: this browser stops getting my
      // pushes (private reminders on a shared screen). Bounded, never throws.
      const lifetime = captureAuthLifetime();
      try {
        await releasePushDevice();
        if (!isAuthLifetimeCurrent(lifetime)) throw sessionChangedError();
        await api<void>('/api/auth/logout', { method: 'POST', body: { allSessions }, skipRefresh: true, refreshExpiredAccess: true, allowAfterSessionEnds: true });
      } finally {
        // Explicit local sign-out still works offline, but never clears a
        // replacement account that signed in while push cleanup was pending.
        if (isAuthLifetimeCurrent(lifetime)) { clear(); qc.clear(); }
      }
    },
  });
}

/** POST the verify-email token (single use — call once per token; see useOneShotToken). */
export function confirmEmail(input: ConfirmEmailInput): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/auth/email/verify/confirm', {
    method: 'POST',
    body: input,
    unauthenticated: true,
  });
}

export function useConfirmEmail() {
  return useMutation({ mutationFn: confirmEmail });
}

export function useResendVerificationEmail() {
  return useMutation({
    mutationFn: () =>
      api<void>('/api/auth/email/verify/request', {
        method: 'POST',
        body: {},
      }),
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (input: RequestPasswordResetInput) =>
      api<void>('/api/auth/password/reset/request', {
        method: 'POST',
        body: input,
        unauthenticated: true,
      }),
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (input: ConfirmPasswordResetInput) =>
      api<void>('/api/auth/password/reset/confirm', {
        method: 'POST',
        body: input,
        unauthenticated: true,
      }),
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api<void>('/api/auth/password/change', { method: 'POST', body: input, skipRefresh: true, refreshExpiredAccess: true, allowAfterSessionEnds: true }),
    onSuccess: () => finishSecurityChange(qc, 'password_changed'),
  });
}

/** Request an email change — sends a verification link to the new address.
 *  Authenticated; gated by the current password. */
export function useRequestEmailChange() {
  return useMutation({
    mutationFn: (input: RequestEmailChangeInput) =>
      api<void>('/api/auth/email/change/request', { method: 'POST', body: input, skipRefresh: true, refreshExpiredAccess: true }),
  });
}

/** Confirm an email change from the link in the new address's inbox. */
/** POST the email-change token (single use — call once per token; see useOneShotToken). */
export function confirmEmailChange(input: ConfirmEmailChangeInput): Promise<ConfirmEmailChangeResult> {
  return api<ConfirmEmailChangeResult>('/api/auth/email/change/confirm', {
    method: 'POST',
    body: input,
    unauthenticated: true,
  });
}

export function useConfirmEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: confirmEmailChange,
    // A public link may belong to another account. Re-read the current viewer;
    // never copy the confirmed target email into an unrelated signed-in user.
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['me'], exact: true }); },
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: (input: DeleteAccountInput) =>
      api<void>('/api/auth/delete-account', { method: 'POST', body: input }),
  });
}

export function useEnrol2fa() {
  return useMutation({
    mutationFn: (input: Enroll2faInput) => api<Enrol2faResponse>('/api/auth/2fa/enroll', { method: 'POST', body: input, skipRefresh: true, refreshExpiredAccess: true }),
  });
}

export function useConfirm2fa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Confirm2faInput) =>
      api<BackupCodes>('/api/auth/2fa/confirm', { method: 'POST', body: input, skipRefresh: true, refreshExpiredAccess: true, allowAfterSessionEnds: true }),
    onSuccess: (result) => finishSecurityChange(qc, 'two_factor_enabled', result.backupCodes),
  });
}

/** Remaining unredeemed recovery codes (Account → Security card). */
export function useRecoveryCodesStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['2fa', 'recovery-codes'],
    queryFn: () => api<RecoveryCodesStatus>('/api/auth/2fa/recovery-codes'),
    enabled,
  });
}

/** Regenerate recovery codes (requires a current TOTP code); returns the new
 *  set once for display. */
export function useRegenerateRecoveryCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Confirm2faInput) =>
      api<BackupCodes>('/api/auth/2fa/recovery-codes/regenerate', { method: 'POST', body: input, skipRefresh: true, refreshExpiredAccess: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['2fa', 'recovery-codes'] });
    },
  });
}

/** Sign in with a recovery code instead of the authenticator TOTP. */
export function useSignin2faRecovery() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    // The login page renders its own error UI (toast / 429 countdown) — skip
    // the global mutation toast so errors don't show twice.
    meta: { silentErrors: true },
    mutationFn: (input: Signin2faRecoveryInput) =>
      api<SignedIn>('/api/auth/signin/2fa/recovery', {
        method: 'POST',
        body: input,
        unauthenticated: true,
      }),
    onSuccess: (s) => setSession(s),
  });
}

export function useDisable2fa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Confirm2faInput) =>
      api<void>('/api/auth/2fa', { method: 'DELETE', body: input, skipRefresh: true, refreshExpiredAccess: true, allowAfterSessionEnds: true }),
    onSuccess: () => finishSecurityChange(qc, 'two_factor_disabled'),
  });
}

/** Unlike a local sign-out, a failed revoke-all must leave the controls available
 * to retry and must not claim that other devices have been signed out. */
export function useSignOutEverywhere() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const lifetime = captureAuthLifetime();
      await releasePushDevice();
      if (!isAuthLifetimeCurrent(lifetime)) throw sessionChangedError();
      return api<void>('/api/auth/logout', { method: 'POST', body: { allSessions: true }, skipRefresh: true, refreshExpiredAccess: true, allowAfterSessionEnds: true });
    },
    onSuccess: () => finishSecurityChange(qc, 'all_sessions_revoked'),
  });
}

function sessionChangedError(): ApiError {
  return new ApiError(401, { error: { code: 'session_changed', message: tr(apiMessages, 'sessionChanged') } });
}

// ── queries ──────────────────────────────────────────────────────────────────

export function useMe() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const me = await api<AuthenticatedUserWithProfile>('/api/me');
      // A chosen profile language wins across devices; a pre-auth pick or a
      // choice cached on this device is saved to an unchosen profile; else the
      // browser language is used and reported as detected (see i18n/boot.ts).
      const localePatch = syncProfileLocale(me.locale, me.detectedLocale);
      if (localePatch) {
        Object.assign(me, localePatch);
        void api('/api/me', { method: 'PATCH', body: localePatch }).catch(() => undefined);
      }
      // Mirror the latest snapshot into the auth store for instant render on next reload.
      useAuthStore.getState().setUser(me);
      return me;
    },
    enabled: !!accessToken,
    staleTime: 60 * 1000,
  });
}

/** What /api/me returns — fuller profile than the auth-flow `AuthenticatedUser`. */
export interface AuthenticatedUserWithProfile extends AuthenticatedUser {
  bio: string | null;
  isBot: boolean;
  timezone: string;
  /** The UI language the person chose; null = not chosen (follow the browser). */
  locale: string | null;
  /** The language the web app last detected while `locale` is null (server copy only). */
  detectedLocale?: string | null;
  theme: 'light' | 'dark' | 'system';
  density: 'compact' | 'default' | 'comfortable';
  /**
   * Mobile landing pref (internal ids; labels come from the glossary).
   * `'auto'` (the default since 0116) = the server's `mobileLandingTab`.
   */
  mobileDefaultTab: 'auto' | 'dashboard' | 'tasks' | 'chat' | 'workspace';
  /** The concrete tab `'auto'` resolves to (My Work, or Home when Lead+ in a regular team). */
  mobileLandingTab?: 'dashboard' | 'tasks' | 'chat' | 'workspace';
  discoverableByUsername: boolean;
  discoverableByEmail: boolean;
  /** Discovery & Messaging (Sprint 1.2). */
  whoCanFindMe: 'everyone' | 'teams' | 'exact' | 'nobody';
  whoCanMessage: 'anyone' | 'contacts' | 'requests';
  /** Sprint 2.3 — first-run checklist dismissed forever. */
  onboardingDismissed: boolean;
  githubLogin: string | null;
  createdAt: string;
  updatedAt: string;
  /** Plan-feature entitlements (personal plan OR any team) — drives FE locks. */
  entitlements: { public_api: boolean };
}
